import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BRANCH_CODES, BRANCH_LABELS, type BranchCode } from '@lorsamthai/ops-shared';
import { listStock, type StockRow } from '../lib/queries.js';
import { Badge } from '../ui/Badge.js';
import { fmtInt, fmtDate } from '../lib/format.js';
import { resolveSkuDisplay } from '../lib/skuParser.js';

export default function Stock() {
  const [rows, setRows] = useState<StockRow[]>([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [branch, setBranch] = useState<BranchCode | ''>('');
  const [belowMin, setBelowMin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listStock({
        q: q || undefined,
        branch: branch || undefined,
        below_min: belowMin,
        limit: 100,
      });
      setRows(res.items);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'load failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-bold">Stock</h1>
        <Link
          to="/stock/import"
          className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm hover:bg-slate-50"
          title="ดาวน์โหลด template และ bulk import min/target/supplier"
        >
          📥 Import targets
        </Link>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()}
          placeholder="ค้นหา SKU..."
          className="min-w-[240px] flex-1 rounded-md border border-slate-200 px-3 py-1.5 text-sm"
        />
        <select
          value={branch}
          onChange={(e) => setBranch(e.target.value as BranchCode | '')}
          className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm"
        >
          <option value="">ทุกสาขา</option>
          {BRANCH_CODES.map((b) => (
            <option key={b} value={b}>
              {BRANCH_LABELS[b]}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={belowMin}
            onChange={(e) => setBelowMin(e.target.checked)}
          />
          เฉพาะต่ำกว่า min
        </label>
        <button
          onClick={load}
          disabled={loading}
          className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
        >
          {loading ? '…' : 'ค้นหา'}
        </button>
      </div>

      {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">SKU</th>
              <th className="px-3 py-2">ขนาดยาง</th>
              <th className="px-3 py-2">สินค้า</th>
              <th className="px-3 py-2">สาขา</th>
              <th className="px-3 py-2 text-right">ในสต็อก</th>
              <th className="px-3 py-2 text-right">กำลังมา</th>
              <th className="px-3 py-2 text-right">Effective</th>
              <th className="px-3 py-2 text-right" title="นับเฉพาะยาง DOT ปีปัจจุบัน — ตัวเลขที่ใช้ตัดสินใจสั่ง/โอน">
                DOT ปีนี้
              </th>
              <th className="px-3 py-2 text-right">Min</th>
              <th className="px-3 py-2 text-right">Target</th>
              <th className="px-3 py-2">สถานะ</th>
              <th className="px-3 py-2">ETA</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={12} className="px-3 py-8 text-center text-slate-500">
                  ไม่มีข้อมูล
                </td>
              </tr>
            )}
            {rows.map((r) => {
              // สถานะอิงยาง DOT ปีปัจจุบัน ให้ตรงกับเกณฑ์ที่หน้า Order Day ใช้
              const low = r.effective_cy < r.min_qty;
              const critical = r.effective_cy === 0;
              const display = resolveSkuDisplay(r.sku, r.brand, r.model, r.size);
              const product = [display.brand, display.model].filter(Boolean).join(' ') || '—';
              return (
                <tr key={`${r.sku}:${r.branch}`} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs">
                    <Link to={`/stock/${encodeURIComponent(r.sku)}`} className="hover:underline">
                      {r.sku}
                    </Link>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-700">{display.size ?? '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-700">{product}</td>
                  <td className="px-3 py-2">{BRANCH_LABELS[r.branch]}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtInt(r.on_hand)}</td>
                  <td className="px-3 py-2 text-right font-mono">
                    {r.incoming_po > 0 ? (
                      <Badge tone="info">+{r.incoming_po}</Badge>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{fmtInt(r.effective)}</td>
                  <td
                    className="px-3 py-2 text-right font-mono font-semibold"
                    title={r.on_hand_old > 0 ? `DOT ปีเก่าค้าง ${r.on_hand_old} เส้น` : undefined}
                  >
                    {fmtInt(r.effective_cy)}
                    {r.on_hand_old > 0 && (
                      <span className="ml-1 text-xs font-normal text-amber-700">
                        (เก่า {fmtInt(r.on_hand_old)})
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-slate-600">{fmtInt(r.min_qty)}</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-500">{fmtInt(r.target_qty)}</td>
                  <td className="px-3 py-2">
                    {critical ? (
                      <Badge tone="danger">หมด</Badge>
                    ) : low ? (
                      <Badge tone="low">ต่ำกว่า min</Badge>
                    ) : (
                      <Badge tone="ok">ปกติ</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(r.next_eta)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-slate-500">
        แสดง {rows.length} / ทั้งหมด {fmtInt(total)} แถว
      </div>
    </div>
  );
}
